import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Clock, Phone, ChevronDown, Users, Calendar, Award, Zap, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/shared/Header';
import Footer from '@/components/shared/Footer';
import TestimonialCard from '@/components/shared/TestimonialCard';
import { DCA_LINKS, PACKAGE_ENROLL_LINKS } from '@/lib/dcaConfig';
import JsonLd, { generateFAQSchema, generateServiceSchema } from '@/components/seo/JsonLd';
import { careerCoachingPackages } from '@/data/servicesData';
import { publicApi } from '@/lib/api';

const CareerCoachingPackagesPage = () => {
  const data = careerCoachingPackages;
  const [openFaq, setOpenFaq] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const IconComponent = data.icon;

  useEffect(() => {
    publicApi.getTestimonials()
      .then(res => setTestimonials(res.data))
      .catch(console.error);
  }, []);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Career Coaching Packages | SEG Career Support</title>
        <meta name="description" content="Long-term career coaching partnerships with 3-month, 6-month, and 12-month programs. Get consistent guidance, measurable progress, and accountability." />
        <meta property="og:title" content="Career Coaching Packages | SEG Career Support" />
        <meta property="og:description" content="Choose from 3-month Career Acceleration, 6-month Growth Partnership, or 12-month Executive Development programs." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://segcareersupport.com/coaching/career-coaching-packages" />
      </Helmet>
      
      {/* JSON-LD Structured Data */}
      <JsonLd data={generateFAQSchema(data.faqs)} />
      <JsonLd data={generateServiceSchema({ title: 'Career Coaching Packages', short_description: data.heroDescription })} />
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary to-primary/90 text-white overflow-hidden" data-testid="hero-section">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')] opacity-50"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-2 text-sm mb-6">
                  <IconComponent className="w-4 h-4" />
                  <span>Long-Term Career Partnerships</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-4">
                  {data.title}
                </h1>
                <p className="text-xl md:text-2xl text-white/90 mb-6">
                  {data.tagline}
                </p>
                <p className="text-lg text-white/80 mb-8 max-w-xl">
                  {data.heroDescription}
                </p>
                <div className="flex flex-wrap gap-4">
                  <Button 
                    size="lg" 
                    className="bg-accent hover:bg-accent/90 text-white"
                    data-testid="hero-book-consultation-btn"
                    onClick={() => window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer')}
                  >
                    Free 20-Minute Career Strategy Session <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="border-white text-white hover:bg-white/10"
                  >
                    <Phone className="w-5 h-5 mr-2" /> (912) 376-9006
                  </Button>
                </div>
              </div>
              <div className="relative hidden lg:block">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={data.image}
                    alt={data.title}
                    className="w-full h-auto"
                  />
                </div>
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-accent rounded-xl"></div>
                <div className="absolute -top-4 -right-4 w-16 h-16 bg-secondary/50 rounded-xl"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Navigation */}
        <section className="py-4 bg-muted/50 border-b sticky top-16 z-40" data-testid="quick-nav">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4">
              <button
                onClick={() => scrollToSection('strategy-session')}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Strategy Session
              </button>
              <span className="hidden sm:inline text-muted-foreground/50">|</span>
              <button
                onClick={() => scrollToSection('3-month')}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                3-Month
              </button>
              <span className="hidden sm:inline text-muted-foreground/50">|</span>
              <button
                onClick={() => scrollToSection('6-month')}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                6-Month
              </button>
              <span className="hidden sm:inline text-muted-foreground/50">|</span>
              <button
                onClick={() => scrollToSection('executive')}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                Executive
              </button>
              <span className="hidden sm:inline text-muted-foreground/50">|</span>
              <button
                onClick={() => scrollToSection('faq')}
                className="px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
              >
                FAQ
              </button>
            </div>
          </div>
        </section>

        {/* Single Session Callout */}
        <section id="strategy-session" className="py-16 bg-gradient-to-r from-accent/10 to-primary/10" data-testid="strategy-session-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-2xl shadow-lg border p-8 md:p-10">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <span className="inline-block bg-accent/10 text-accent px-3 py-1 rounded-full text-sm font-medium mb-4">
                    Start Here
                  </span>
                  <h2 className="text-2xl md:text-3xl font-heading font-bold mb-4">
                    Career Strategy Session
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Not ready for a long-term commitment? Start with a single 60-minute strategy session. 
                    Get comprehensive guidance on any career topic with a clear action plan.
                  </p>
                  <div className="flex items-center gap-6 mb-6">
                    <div>
                      <span className="text-3xl font-bold text-primary">$150</span>
                      <span className="text-muted-foreground"> / 60 min</span>
                    </div>
                  </div>
                  <Button onClick={() => window.open(DCA_LINKS.session, '_blank', 'noopener,noreferrer')} data-testid="strategy-session-book-btn">
                    Book This Session <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="hidden lg:flex items-center justify-center">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/50 rounded-lg p-4 text-center">
                      <Clock className="w-6 h-6 text-primary mx-auto mb-2" />
                      <p className="text-sm font-medium">60 Minutes</p>
                      <p className="text-xs text-muted-foreground">In-depth session</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 text-center">
                      <Zap className="w-6 h-6 text-primary mx-auto mb-2" />
                      <p className="text-sm font-medium">Action Plan</p>
                      <p className="text-xs text-muted-foreground">Within 24 hours</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 text-center">
                      <Users className="w-6 h-6 text-primary mx-auto mb-2" />
                      <p className="text-sm font-medium">Any Topic</p>
                      <p className="text-xs text-muted-foreground">Fully customized</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-4 text-center">
                      <Award className="w-6 h-6 text-primary mx-auto mb-2" />
                      <p className="text-sm font-medium">Expert Coach</p>
                      <p className="text-xs text-muted-foreground">Certified strategist</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Coaching Packages */}
        <section className="py-16" data-testid="packages-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Choose Your Coaching Path
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Select the program that matches your career goals and timeline.
              </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-8">
              {data.packages.map((pkg) => (
                <div 
                  key={pkg.id}
                  id={pkg.id}
                  className={`bg-white rounded-2xl shadow-lg border overflow-hidden transition-all hover:shadow-xl ${pkg.highlight ? 'ring-2 ring-accent relative' : ''}`}
                  data-testid={`package-${pkg.id}`}
                >
                  {pkg.highlight && (
                    <div className="absolute top-0 left-0 right-0 bg-accent text-white text-center py-2 text-sm font-medium">
                      Most Popular
                    </div>
                  )}
                  
                  <div className={`p-8 ${pkg.highlight ? 'pt-12' : ''}`}>
                    <div className="mb-6">
                      <span className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium mb-3">
                        {pkg.duration}
                      </span>
                      <h3 className="text-xl font-heading font-bold mb-2">{pkg.title}</h3>
                      <p className="text-muted-foreground text-sm">
                        <span className="font-medium">Ideal for:</span> {pkg.idealFor}
                      </p>
                      {pkg.price && (
                        <div className="mt-4" data-testid={`package-price-${pkg.id}`}>
                          <span className="text-4xl font-heading font-bold text-primary">{pkg.price}</span>
                          <span className="text-muted-foreground text-sm ml-2">total / {pkg.duration}</span>
                        </div>
                      )}
                    </div>

                    <div className="mb-6">
                      <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground mb-3">
                        What's Included
                      </h4>
                      <ul className="space-y-2">
                        {pkg.includes.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-6 bg-muted/50 rounded-lg p-4">
                      <h4 className="font-medium text-sm uppercase tracking-wide text-muted-foreground mb-3">
                        Outcomes
                      </h4>
                      <ul className="space-y-2">
                        {pkg.outcomes.map((outcome, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <Award className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                            <span>{outcome}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Button 
                      className={`w-full ${pkg.highlight ? 'bg-accent hover:bg-accent/90' : ''}`}
                      size="lg"
                      onClick={() => window.open(PACKAGE_ENROLL_LINKS[pkg.id] || DCA_LINKS.packages, '_blank', 'noopener,noreferrer')}
                      data-testid={`package-cta-${pkg.id}`}
                    >
                      {pkg.ctaText} <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-muted/30" data-testid="how-it-works-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                How It Works
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {data.processIntro}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {data.processSteps.map((step, index) => (
                <div key={step.number} className="relative">
                  {index < data.processSteps.length - 1 && (
                    <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-primary to-accent -translate-x-1/2 z-0"></div>
                  )}
                  <div className="bg-white rounded-xl p-6 shadow-md border relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                      {step.number}
                    </div>
                    <h3 className="text-lg font-heading font-bold mb-2 text-center">{step.title}</h3>
                    <p className="text-muted-foreground text-sm text-center">{step.description}</p>
                    {step.timeframe && (
                      <p className="text-center mt-3">
                        <span className="inline-flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-1 rounded">
                          <Clock className="w-3 h-3" />
                          {step.timeframe}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Success Stories / Testimonials */}
        {testimonials.length > 0 && (
          <section className="py-16 bg-gradient-to-br from-primary/5 to-accent/5" data-testid="testimonials-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                  Success Stories
                </h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Hear from professionals who transformed their careers with our coaching.
                </p>
              </div>

              <div className="relative max-w-4xl mx-auto">
                <div className="flex items-center justify-center">
                  {testimonials.map((testimonial, index) => (
                    <div
                      key={testimonial.id}
                      className={`w-full transition-all duration-500 ${
                        index === currentTestimonial ? 'opacity-100' : 'opacity-0 absolute'
                      }`}
                    >
                      <TestimonialCard testimonial={testimonial} />
                    </div>
                  ))}
                </div>

                {testimonials.length > 1 && (
                  <div className="flex items-center justify-center gap-4 mt-8">
                    <button
                      onClick={prevTestimonial}
                      className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow border"
                      data-testid="testimonial-prev"
                    >
                      <ChevronLeft className="w-5 h-5 text-primary" />
                    </button>
                    
                    <div className="flex gap-2">
                      {testimonials.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentTestimonial(index)}
                          className={`w-2.5 h-2.5 rounded-full transition-all ${
                            index === currentTestimonial ? 'bg-primary w-6' : 'bg-muted-foreground/30'
                          }`}
                        />
                      ))}
                    </div>
                    
                    <button
                      onClick={nextTestimonial}
                      className="p-2 rounded-full bg-white shadow-md hover:shadow-lg transition-shadow border"
                      data-testid="testimonial-next"
                    >
                      <ChevronRight className="w-5 h-5 text-primary" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section id="faq" className="py-16" data-testid="faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-muted-foreground">
                Everything you need to know about our coaching programs.
              </p>
            </div>

            <div className="space-y-4">
              {data.faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl border shadow-sm overflow-hidden"
                  data-testid={`faq-item-${index}`}
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/30 transition-colors"
                  >
                    <span className="font-medium pr-4">{faq.question}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${
                        openFaq === index ? 'rotate-180' : ''
                      }`} 
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-4 text-muted-foreground">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-gradient-to-r from-primary to-secondary text-white py-16" data-testid="final-cta">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-4">
              Ready to Stop Guessing and Start Planning?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Your next career move deserves strategy — not uncertainty.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-accent hover:bg-accent/90"
                onClick={() => window.open(DCA_LINKS.free20, '_blank', 'noopener,noreferrer')}
              >
                Free 20-Minute Career Strategy Session <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-white text-white hover:bg-white/10"
                asChild
              >
                <Link to="/contact-us">Contact SEG Career Support</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default CareerCoachingPackagesPage;
